import * as THREE from "three";
import { useLoader } from "@react-three/fiber";

/**
 * Enclosed mesh office for the WASD walkable PoC.
 *
 * This is REAL geometry with real collision (see WALKABLE_OFFICE_COLLIDERS below),
 * not a Marble/Gaussian-splat reconstruction. A single-photo splat only has data
 * near its one capture point, so it can never support true free walking — that is
 * why this mesh path exists and is the one office-walkable.scenario.ts must use.
 * Surfaces use real photographic textures (floor/desk/wall) instead of flat
 * "toy" colors so it reads as a real room while walking is 100% guaranteed to work
 * everywhere in the room (only bounded by the outer-wall/furniture AABBs).
 *
 * Keep visuals and colliders in sync when editing layout.
 */

export type Aabb = {
  min: [number, number, number];
  max: [number, number, number];
};

const TEX_BASE = "/cdn/sim/office-walkable/tex";

/** Outer walls + solid furniture the player cannot walk through. */
export const WALKABLE_OFFICE_COLLIDERS: Aabb[] = [
  // Outer walls (room ~10 x 8, floor y=0, ceiling ~3.2)
  { min: [-5.1, 0, -4.1], max: [5.1, 3.2, -3.9] }, // back
  { min: [-5.1, 0, 3.9], max: [5.1, 3.2, 4.1] }, // front
  { min: [-5.1, 0, -4.1], max: [-4.9, 3.2, 4.1] }, // left
  { min: [4.9, 0, -4.1], max: [5.1, 3.2, 4.1] }, // right
  // Hero desk
  { min: [-1.3, 0, -1.0], max: [1.3, 0.85, 0.35] },
  // Printer cabinet
  { min: [-4.2, 0, -1.2], max: [-2.8, 1.4, 0.2] },
  // Side desks
  { min: [2.2, 0, -2.8], max: [4.2, 0.85, -1.4] },
  { min: [2.2, 0, 1.0], max: [4.2, 0.85, 2.4] },
  // Filing cabinets
  { min: [-4.4, 0, 2.0], max: [-3.4, 1.6, 3.2] },
];

function useOfficeTextures() {
  const [floor, desk, wall] = useLoader(THREE.TextureLoader, [
    `${TEX_BASE}/floor.png`,
    `${TEX_BASE}/desk.png`,
    `${TEX_BASE}/wall.png`,
  ]);
  floor.wrapS = floor.wrapT = THREE.RepeatWrapping;
  floor.repeat.set(5, 4);
  floor.colorSpace = THREE.SRGBColorSpace;
  desk.wrapS = desk.wrapT = THREE.RepeatWrapping;
  desk.repeat.set(1, 1);
  desk.colorSpace = THREE.SRGBColorSpace;
  wall.wrapS = wall.wrapT = THREE.RepeatWrapping;
  wall.repeat.set(4, 1.6);
  wall.colorSpace = THREE.SRGBColorSpace;
  return { floor, desk, wall };
}

function Box({
  pos,
  size,
  color,
  roughness = 0.85,
  metalness = 0,
}: {
  pos: [number, number, number];
  size: [number, number, number];
  color: string;
  roughness?: number;
  metalness?: number;
}) {
  return (
    <mesh position={pos} castShadow receiveShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial color={color} roughness={roughness} metalness={metalness} />
    </mesh>
  );
}

function Desk({
  at,
  deskTex,
}: {
  at: [number, number, number];
  deskTex: THREE.Texture;
}) {
  const [x, , z] = at;
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, 0.74, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.2, 0.05, 1.0]} />
        <meshStandardMaterial map={deskTex} roughness={0.45} metalness={0.02} />
      </mesh>
      {(
        [
          [-1.0, 0.37, 0.4],
          [1.0, 0.37, 0.4],
          [-1.0, 0.37, -0.4],
          [1.0, 0.37, -0.4],
        ] as [number, number, number][]
      ).map((p, i) => (
        <Box key={i} pos={p} size={[0.06, 0.74, 0.06]} color="#1a1f26" metalness={0.35} roughness={0.45} />
      ))}
      <group position={[0, 0.98, -0.28]}>
        <Box pos={[0, 0, 0]} size={[0.58, 0.36, 0.04]} color="#0a0d11" roughness={0.4} />
        <mesh position={[0, 0, 0.025]}>
          <planeGeometry args={[0.52, 0.3]} />
          <meshStandardMaterial color="#0b3d6e" emissive={new THREE.Color("#061f38")} emissiveIntensity={0.35} />
        </mesh>
      </group>
      <Box pos={[0, 0.77, 0.18]} size={[0.42, 0.02, 0.15]} color="#1c2228" />
      <Box pos={[0.55, 0.78, -0.05]} size={[0.18, 0.05, 0.22]} color="#15191f" />
    </group>
  );
}

function Printer() {
  return (
    <group position={[-3.5, 0, -0.5]}>
      <Box pos={[0, 0.45, 0]} size={[1.1, 0.9, 0.9]} color="#e8eaed" roughness={0.7} />
      <Box pos={[0, 1.05, 0.05]} size={[0.95, 0.28, 0.7]} color="#d0d4da" />
      <Box pos={[0.2, 1.28, 0.15]} size={[0.35, 0.08, 0.25]} color="#2a3038" />
      <mesh position={[0, 1.22, 0.42]}>
        <planeGeometry args={[0.4, 0.12]} />
        <meshStandardMaterial color="#1a8f6a" emissive={new THREE.Color("#0d4a38")} emissiveIntensity={0.5} />
      </mesh>
    </group>
  );
}

export function WalkableOffice() {
  const { floor, desk, wall } = useOfficeTextures();
  return (
    <group>
      {/* Floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[10, 8]} />
        <meshStandardMaterial map={floor} roughness={0.95} />
      </mesh>
      {/* Ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 3.15, 0]} receiveShadow>
        <planeGeometry args={[10, 8]} />
        <meshStandardMaterial color="#eef1f4" roughness={1} />
      </mesh>
      {/* Ceiling lights */}
      {(
        [
          [-2, 3.05, -1.5],
          [2, 3.05, -1.5],
          [-2, 3.05, 1.5],
          [2, 3.05, 1.5],
        ] as [number, number, number][]
      ).map((p, i) => (
        <mesh key={i} position={p}>
          <boxGeometry args={[1.4, 0.06, 0.45]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive={new THREE.Color("#f5f7fa")}
            emissiveIntensity={1.2}
            roughness={0.4}
          />
        </mesh>
      ))}

      {/* Walls */}
      <mesh position={[0, 1.6, -4]} receiveShadow>
        <boxGeometry args={[10.2, 3.2, 0.2]} />
        <meshStandardMaterial map={wall} roughness={0.92} />
      </mesh>
      <mesh position={[0, 1.6, 4]} receiveShadow>
        <boxGeometry args={[10.2, 3.2, 0.2]} />
        <meshStandardMaterial map={wall} roughness={0.92} />
      </mesh>
      <mesh position={[-5, 1.6, 0]} receiveShadow>
        <boxGeometry args={[0.2, 3.2, 8.2]} />
        <meshStandardMaterial map={wall} roughness={0.92} />
      </mesh>
      <mesh position={[5, 1.6, 0]} receiveShadow>
        <boxGeometry args={[0.2, 3.2, 8.2]} />
        <meshStandardMaterial map={wall} roughness={0.92} />
      </mesh>

      {/* Corkboard + whiteboard on back wall */}
      <Box pos={[-1.8, 1.9, -3.88]} size={[1.6, 1.1, 0.06]} color="#c4a574" />
      <Box pos={[1.6, 1.95, -3.88]} size={[2.0, 1.2, 0.05]} color="#f7f8fa" />
      <Box pos={[1.6, 1.95, -3.85]} size={[1.85, 1.05, 0.02]} color="#ffffff" />

      {/* Doors on left wall (visual) */}
      {([-2.2, 0, 2.2] as number[]).map((z, i) => (
        <group key={i} position={[-4.88, 0, z]}>
          <Box pos={[0, 1.1, 0]} size={[0.08, 2.2, 1.0]} color="#6b4e35" roughness={0.65} />
          <mesh position={[0.06, 1.1, 0.35]}>
            <sphereGeometry args={[0.04, 12, 12]} />
            <meshStandardMaterial color="#c0c4c8" metalness={0.7} roughness={0.3} />
          </mesh>
        </group>
      ))}

      <Desk at={[0, 0, -0.3]} deskTex={desk} />
      <Desk at={[3.2, 0, -2.1]} deskTex={desk} />
      <Desk at={[3.2, 0, 1.7]} deskTex={desk} />
      <Printer />

      {/* Filing cabinet */}
      <Box pos={[-3.9, 0.8, 2.6]} size={[0.9, 1.6, 1.1]} color="#4a5562" metalness={0.25} roughness={0.55} />

      {/* Plant */}
      <mesh position={[4.2, 0.35, 3.2]} castShadow>
        <cylinderGeometry args={[0.18, 0.22, 0.35, 16]} />
        <meshStandardMaterial color="#6b4f3a" />
      </mesh>
      <mesh position={[4.2, 0.85, 3.2]} castShadow>
        <sphereGeometry args={[0.35, 16, 12]} />
        <meshStandardMaterial color="#2f6b3a" roughness={0.9} />
      </mesh>
    </group>
  );
}
